import React, { type ComponentType, type ReactNode } from 'react';

type ProviderEntry = {
  Provider: ComponentType<any>;
  props?: Record<string, unknown>;
};

/**
 * Type-safe helper that provides IntelliSense for provider props.
 *
 * @example
 * provider(SafeAreaProvider)
 * provider(ThemeProvider, { theme: darkTheme })
 */
export function provider<P extends object>(
  Provider: ComponentType<P & { children: ReactNode }>,
  props?: Omit<P, 'children'>,
): ProviderEntry {
  return { Provider, props: props as Record<string, unknown> };
}

type ComposeProvidersProps = {
  providers: ProviderEntry[];
  children: ReactNode;
};

/**
 * @example
 * <ComposeProviders
 *   providers={[
 *     provider(SafeAreaProvider),
 *     provider(QueryProvider),
 *     provider(ThemeProvider, { theme: darkTheme }),
 *   ]}>
 *   <App />
 * </ComposeProviders>
 */
export const ComposeProviders = ({ providers, children }: ComposeProvidersProps) => {
  return providers.reduceRight<ReactNode>((acc, { Provider, props }) => {
    const { key, ...rest } = props ?? {};
    return (
      <Provider key={key} {...rest}>
        {acc}
      </Provider>
    );
  }, children);
};
